import { Outlet } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Sidebar } from './Sidebar';
import { CommandPalette } from './CommandPalette';
import { MobileNav } from './MobileNav';
import { Menu } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useBranding } from '@/hooks/useBranding';

export const AppLayout = () => {
  const { branding } = useBranding();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);

  // Auto-close sidebar on mobile, auto-open on desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024) {
        setSidebarOpen(false);
      } else {
        setSidebarOpen(true);
      }
    };

    handleResize(); // Initial check
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close sidebar when clicking outside on mobile
  const handleMobileSidebarClose = () => {
    if (window.innerWidth < 1024) {
      setSidebarOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Command Palette - Global keyboard shortcut Cmd+K / Ctrl+K */}
      <CommandPalette
        open={commandPaletteOpen}
        onOpenChange={setCommandPaletteOpen}
      />

      <div className="flex lg:h-screen lg:overflow-hidden">
        {/* Mobile sidebar overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-30 lg:hidden"
            onClick={handleMobileSidebarClose}
          />
        )}

        {/* Sidebar - hidden on mobile unless opened */}
        <div className={`${sidebarOpen ? 'block' : 'hidden'} lg:block`}>
          <Sidebar
            collapsed={!sidebarOpen}
            onToggle={() => setSidebarOpen(!sidebarOpen)}
          />
        </div>

        {/* Main content area */}
        <div className="flex-1 flex flex-col lg:min-h-screen lg:h-screen lg:overflow-hidden lg:ml-0">
          {/* Header with mobile menu - sticky on mobile */}
          <header className="border-b border-border bg-card/50 backdrop-blur-lg sticky top-0 z-40">
            <div className="flex items-center justify-between px-4 py-4">
              {/* Mobile menu button - 44x44 touch target */}
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden min-h-[44px] min-w-[44px] mr-3"
                onClick={() => setSidebarOpen(!sidebarOpen)}
              >
                <Menu className="h-6 w-6" />
              </Button>

              <h1 className="text-xl font-bold text-foreground flex-1 lg:text-2xl">
                {branding.appName}
              </h1>

              {/* Command palette trigger hint */}
              <Button
                variant="outline"
                size="sm"
                className="hidden md:flex gap-2 text-muted-foreground"
                onClick={() => setCommandPaletteOpen(true)}
              >
                <span className="text-xs">⌘K</span>
                <span className="text-xs hidden lg:inline">Quick search</span>
              </Button>
            </div>
          </header>

          {/* Main content with scroll - padding bottom for mobile nav */}
          <main className="flex-1 overflow-y-auto px-4 py-6 pb-24 lg:px-8 lg:py-8 lg:pb-8">
            <div className="page-transition">
              <Outlet />
            </div>
          </main>
        </div>
      </div>

      {/* Mobile bottom navigation */}
      <MobileNav />
    </div>
  );
};
